import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';
import type { CheckoutState } from './checkoutTypes';
import type { MomoNetwork } from './utils/ghanaMomo';

export interface PublicPaymentLinkSuggestedAmount {
  kind: string;
  amount: string;
  isDefault: boolean;
}

export interface PublicPaymentLinkDetail {
  token: string;
  school: {
    id: number;
    name: string;
  };
  student: {
    name: string;
    studentNumber: string;
  };
  session: {
    id: number;
    displayName: string;
  };
  amount: string;
  description: string;
  expiresAt: string;
  status: string;
  allowsPartialPayment?: boolean;
  remainingWeeksInSession?: number;
  suggestedAmounts?: PublicPaymentLinkSuggestedAmount[];
  serviceFeeRate?: number;
  momoCheckout?: boolean;
}

export interface InitiatePaymentLinkRequest {
  token: string;
  phoneNumber?: string;
  email?: string;
  amount?: string;
  network?: MomoNetwork;
  startAgain?: boolean;
}

export type InitiatePaymentLinkResponse = Partial<CheckoutState> & {
  nextStep?: string;
  checkoutUrl: string | null;
  success: boolean;
};

export const PublicPaymentLinkApi = createApi({
  reducerPath: 'PublicPaymentLinkApi',
  baseQuery,
  endpoints: builder => ({
    getPublicPaymentLink: builder.query<PublicPaymentLinkDetail, string>({
      query: token => ({
        url: `/pay/${token}`,
      }),
    }),
    initiatePaymentLink: builder.mutation<InitiatePaymentLinkResponse, InitiatePaymentLinkRequest>({
      query: ({ token, phoneNumber, email, amount, network, startAgain }) => ({
        url: `/pay/${token}/initiate`,
        method: 'POST',
        body: {
          ...(phoneNumber ? { phoneNumber } : {}),
          ...(email ? { email } : {}),
          ...(amount ? { amount } : {}),
          ...(network ? { network } : {}),
          ...(startAgain ? { startAgain } : {}),
        },
      }),
    }),
    confirmPaymentLinkOtp: builder.mutation<
      CheckoutState,
      { token: string; otp: string }
    >({
      query: ({ token, otp }) => ({
        url: `/pay/${token}/confirm-otp`,
        method: 'POST',
        body: { otp },
      }),
    }),
    getPaymentLinkCheckoutStatus: builder.query<CheckoutState, string>({
      query: token => ({
        url: `/pay/${token}/status`,
      }),
      keepUnusedDataFor: 0,
      // A person is waiting on "did my money move?": never held by the outage hold.
      extraOptions: { bypassOutageHold: true },
    }),
  }),
});

export const {
  useGetPublicPaymentLinkQuery,
  useInitiatePaymentLinkMutation,
  useConfirmPaymentLinkOtpMutation,
  useLazyGetPaymentLinkCheckoutStatusQuery,
} = PublicPaymentLinkApi;
